import { motion } from "framer-motion";
import { colors, fonts } from "@/lib/theme";

interface HeaderProps {
  title?: string | null;
  description?: string | null;
}

/**
 * Option 2: Growing Circle Frame
 * Concentric circles expand outward with accent dots
 */
export function GrowingCircleHeader({ title, description }: HeaderProps) {
  const hasContent = title || description;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-8 relative overflow-hidden"
      style={{ backgroundColor: colors.surface }}
    >
      {/* Outer circle */}
      <motion.div
        initial={{ width: 100, height: 100, opacity: 0 }}
        whileInView={{ width: "70vmin", height: "70vmin", opacity: 0.2 }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 1.5, ease: [0.4, 0, 0.2, 1] }}
        className="absolute rounded-full border-2"
        style={{ borderColor: colors.primary }}
      />

      {/* Inner circle */}
      <motion.div
        initial={{ width: 100, height: 100, opacity: 0 }}
        whileInView={{ width: "50vmin", height: "50vmin", opacity: 0.15 }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 1.5, ease: [0.4, 0, 0.2, 1], delay: 0.2 }}
        className="absolute rounded-full border-2"
        style={{ borderColor: colors.primary }}
      />

      {/* Accent dots */}
      {[
        { top: "15%", left: "50%", transform: "translateX(-50%)" },
        { bottom: "15%", left: "50%", transform: "translateX(-50%)" },
        { top: "50%", left: "15%", transform: "translateY(-50%)" },
        { top: "50%", right: "15%", transform: "translateY(-50%)" },
      ].map((pos, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.6 }}
          viewport={{ once: false, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="absolute w-2 h-2 rounded-full"
          style={{ backgroundColor: colors.accent, ...pos }}
        />
      ))}

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1], delay: 0.5 }}
        className="text-center z-10 max-w-2xl"
      >
        {title && (
          <h2
            className="text-2xl sm:text-3xl md:text-5xl mb-4 md:mb-6"
            style={{
              fontFamily: fonts.serif,
              color: colors.text,
              fontWeight: 400,
              letterSpacing: "0.03em",
            }}
          >
            {title}
          </h2>
        )}

        {description && (
          <p
            className="text-base md:text-lg"
            style={{
              fontFamily: fonts.sans,
              color: colors.textMuted,
              lineHeight: 1.8,
            }}
          >
            {description}
          </p>
        )}

        {!hasContent && (
          <div
            style={{
              width: 60,
              height: 2,
              backgroundColor: colors.primaryMuted,
              margin: "0 auto",
            }}
          />
        )}
      </motion.div>
    </div>
  );
}
